// Base elegible para cupones y descuentos manuales (spec de promociones). Función
// pura: no toca red ni estado.
import { round2 } from "@/lib/retail/pricing"

export interface EligibleBaseLine {
  /**
   * Aporte EXACTO de la línea a `rawSubtotal`. Con este diseño la oferta ES el precio
   * (spec §6.2), así que es lo que la línea cobra: (precio unitario efectivo cobrado
   * — ya con oferta, base de precio único y redondeo — + modificadores) × cantidad,
   * o el total por peso. NO es el precio normal ni el CARD guardado.
   */
  lineSubtotal: number
  /** La línea ya lleva una oferta no combinable: ningún descuento adicional la toca. */
  nonCombinable: boolean
  isAlcohol: boolean
}

/**
 * Calcula sobre qué monto se aplican los descuentos adicionales:
 * - `couponBase`  = `rawSubtotal` − líneas no combinables − líneas de alcohol (solo si
 *   `excludeAlcoholFromCoupon`).
 * - `manualBase`  = `rawSubtotal` − líneas no combinables. El alcohol nunca la afecta.
 *
 * Una línea no combinable Y de alcohol se resta UNA sola vez de `couponBase`.
 *
 * CAMINO RÁPIDO (importa): cuando ninguna línea queda excluida de una base, se devuelve
 * `rawSubtotal` tal cual, sin re-sumar ni redondear. Un restaurante sin promociones (ni
 * alcohol marcado) debe recibir byte a byte el mismo subtotal que recibía antes de este
 * motor; re-sumar líneas podría moverlo un centavo por punto flotante.
 *
 * Si hay exclusiones, se resta línea por línea con `round2` tras cada resta (así
 * 0.3 − 0.1 − 0.1 da 0.1 y no 0.09999999999999998) y la base se corta en 0: nunca negativa.
 */
export function discountBases(p: {
  rawSubtotal: number
  lines: EligibleBaseLine[]
  excludeAlcoholFromCoupon: boolean
}): { couponBase: number; manualBase: number } {
  const { rawSubtotal, lines, excludeAlcoholFromCoupon } = p

  // Líneas que salen de cada base. Se decide por línea para no restar dos veces.
  const fueraManual = lines.filter((l) => l.nonCombinable)
  const fueraCupon = lines.filter((l) => l.nonCombinable || (excludeAlcoholFromCoupon && l.isAlcohol))

  const restar = (excluidas: EligibleBaseLine[]): number => {
    if (excluidas.length === 0) return rawSubtotal // camino rápido: el mismo número
    let base = rawSubtotal
    for (const l of excluidas) base = round2(base - l.lineSubtotal)
    return Math.max(0, base)
  }

  return { couponBase: restar(fueraCupon), manualBase: restar(fueraManual) }
}
